import { Injectable, Type, ComponentRef, ApplicationRef, createComponent, EmbeddedViewRef, Injector } from '@angular/core';
import { DialogRef } from './dialog-ref.service';

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  private dialogComponentRefs: ComponentRef<any>[] = [];
  private overlayDivs: HTMLDivElement[] = [];
  private backdropDismiss = true;

  constructor(
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  open(component: Type<any>, data?: any): DialogRef {
    const dialogRef = new DialogRef();

    // Create overlay
    const overlayDiv = document.createElement('div');
    overlayDiv.className = 'dialog-overlay';
    // Set z-index based on stack depth
    overlayDiv.style.zIndex = (1000 + this.overlayDivs.length * 10).toString();
    document.body.appendChild(overlayDiv);

    // Create component
    const componentRef = createComponent(component, {
      environmentInjector: this.appRef.injector,
      elementInjector: Injector.create({
        providers: [
          { provide: DialogRef, useValue: dialogRef }
        ],
        parent: this.injector
      })
    });

    if (data) {
      if (data.backdropDismiss !== undefined) {
        this.backdropDismiss = data.backdropDismiss;
      }
      // Same payload shape for every dialog: instance.data + flat writable fields.
      // Skip getter-only props (Object.assign throws on those).
      componentRef.instance.data = data;
      this.assignWritable(componentRef.instance, data);
      (dialogRef as any).data = data;
    }

    // Create dialog container
    const dialogContainer = document.createElement('div');
    dialogContainer.className = 'dialog-container';
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0];
    dialogContainer.appendChild(domElem);
    overlayDiv.appendChild(dialogContainer);

    this.appRef.attachView(componentRef.hostView);
    // Custom host is outside the normal component tree — force an initial CD pass
    componentRef.changeDetectorRef.detectChanges();

    // Push to stacks
    this.dialogComponentRefs.push(componentRef);
    this.overlayDivs.push(overlayDiv);

    // Handle click outside to close (only for topmost dialog)
    overlayDiv.addEventListener('click', (event) => {
      if (
        event.target === overlayDiv &&
        this.backdropDismiss &&
        this.overlayDivs[this.overlayDivs.length - 1] === overlayDiv
      ) {
        dialogRef.close();
      }
    });

    // Handle cleanup
    dialogRef.afterClosed$.subscribe(() => {
      this.removeTopDialogComponentFromBody();
    });

    return dialogRef;
  }

  /** Assign only fields the component instance can actually accept. */
  private assignWritable(target: any, source: Record<string, unknown>): void {
    for (const key of Object.keys(source)) {
      if (key === 'data' || key === 'backdropDismiss') continue;
      const descriptor = this.getPropertyDescriptor(target, key);
      if (descriptor && descriptor.set === undefined && descriptor.writable === false) {
        continue;
      }
      try {
        target[key] = source[key];
      } catch {
        // Ignore non-writable / getter-only collisions
      }
    }
  }

  private getPropertyDescriptor(obj: any, key: string): PropertyDescriptor | undefined {
    let current = obj;
    while (current && current !== Object.prototype) {
      const descriptor = Object.getOwnPropertyDescriptor(current, key);
      if (descriptor) return descriptor;
      current = Object.getPrototypeOf(current);
    }
    return undefined;
  }

  private removeTopDialogComponentFromBody() {
    // Only remove the topmost dialog and overlay
    const componentRef = this.dialogComponentRefs.pop();
    const overlayDiv = this.overlayDivs.pop();
    if (componentRef) {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    }
    if (overlayDiv) {
      overlayDiv.remove();
    }
  }

  // Optionally, a method to close all dialogs (if needed)
  closeAllDialogs() {
    while (this.dialogComponentRefs.length > 0) {
      this.removeTopDialogComponentFromBody();
    }
  }

  // openConfirmDialog(title: string, message: string): Observable<boolean> {
  //   return this.open(ConfirmationComponent, { title, message }).afterClosed$;
  // }
} 